<template>
  <div class="form-container">
    <div class="form-header">
      <h2>Sign Up</h2>
    </div>
    <form @submit.prevent="handleSubmit" class="form">
      <div class="form-section">
        <h3>Basic Information</h3>
        <div class="form-group half-width">
          <label for="first_name" class="required">First Name</label>
          <input
            type="text"
            id="first_name"
            v-model.trim="formData.first_name"
            class="form-control"
            maxlength="80"
            required
          />
        </div>

        <div class="form-group half-width">
          <label for="last_name" class="required">Last Name</label>
          <input
            type="text"
            id="last_name"
            v-model.trim="formData.last_name"
            class="form-control"
            maxlength="80"
            required
          />
        </div>

        <div class="form-group two-thirds-width">
          <label for="email" class="required">Email</label>
          <input
            type="email"
            id="email"
            v-model.trim="formData.email"
            class="form-control"
            maxlength="120"
            required
          />
        </div>

        <div class="form-group third-width">
          <label for="phone">Phone</label>
          <input
            type="tel"
            id="phone"
            v-model.trim="formData.phone"
            class="form-control"
            maxlength="20"
          />
        </div>

        <div class="form-group half-width">
          <label for="password" class="required">Password</label>
          <input
            type="password"
            id="password"
            v-model="formData.password"
            class="form-control"
            placeholder="**********"
            required
          />
          <div class="field-details">
            Password must contain at least one lowercase letter, one uppercase letter, one number, and one special character.
          </div>
        </div>

        <div class="form-group half-width">
          <label for="confirmPassword" class="required">Confirm Password</label>
          <input
            type="password"
            id="confirmPassword"
            v-model="formData.confirmPassword"
            class="form-control"
            placeholder="**********"
            required
          />
        </div>
      </div>

      <div class="form-group">
        <div class="checkbox-container">
          <input
            type="checkbox"
            id="termsAccepted"
            v-model="formData.termsAccepted"
            required
          />
          <label for="termsAccepted" class="required">
            I agree to the <a href="https://acuteimmersive.com/terms-conditions/" target="_blank" rel="noopener noreferrer">Terms and Conditions</a>
          </label>
        </div>
      </div>

      <div class="error-message" v-if="formError || apiError">{{ formError || (apiError.response?.data?.error || apiError.message) }}</div>

      <div class="form-actions vertical">
        <button type="submit" class="submit-button big">Sign Up</button>
        <router-link to="/login" class="link">Already have an account? Log in</router-link>
      </div>
    </form>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useMutation } from '@tanstack/vue-query';
import { validatePassword } from '@/scripts/shared-utils';
import { registerUser } from '@/scripts/services/authService';

const route = useRoute();
const router = useRouter();
const formError = ref('');

const formData = ref({
  first_name: '',
  last_name: '',
  email: '',
  phone: '',
  password: '',
  confirmPassword: '',
  termsAccepted: false
});

const inviteToken = computed(() => route.query.token || '');

const { mutate: signup, error: apiError } = useMutation({
  mutationFn: registerUser,
  onSuccess: () => {
    router.push('/login?signup=success');
  },
  onError: (err) => {
    console.error('Signup failed:', err.response?.data?.error || err.message);
  }
});

const handleSubmit = () => {
  formError.value = '';

  const validation = validatePassword(formData.value.password);
  if (!validation.success) {
    formError.value = validation.error;
    return;
  }

  if (formData.value.password !== formData.value.confirmPassword) {
    formError.value = 'Passwords do not match';
    return;
  }

  if (!formData.value.termsAccepted) {
    formError.value = 'You must agree to the Terms and Conditions';
    return;
  }

  signup({
    invite_token: inviteToken.value,
    first_name: formData.value.first_name,
    last_name: formData.value.last_name,
    email: formData.value.email,
    phone: formData.value.phone,
    password: formData.value.password
  });
};
</script>

<style scoped>
@import '@/styles/shared-form.css';
</style>
